import { elapsedTime } from './elapsedTime'
import { DateTime } from 'luxon'
import { RawUserActivity } from '../models/RawUserActivity'
import { Instance } from 'mobx-state-tree'
import { TextMapValueModel } from '../models/base/activity/activity'

export const eventStrings = {
  Assignment: {
    extended: 'extended the {{title}} assignment',
    created: 'created the {{title}} assignment',
    deleted: 'deleted the {{title}} assignment',
    edited: 'edited the {{title}} assignment',
    nudged: 'nudged users to complete the {{title}} assignment',
  },
  Badge: {
    created: 'created a badge {{name}}',
    edited: 'edited the {{name}} badge',
    deleted: 'deleted the {{name}} badge',
    enabled: 'enabled the {{label}} badge',
    disabled: 'disabled the {{label}} badge',
  },
  Contest: {
    created: 'created a new contest {{name}}',
    started: 'started the {{name}} contest ',
    extended: 'started the {{name}} contest ',
    ended: 'ended the {{name}} contest',
    edited: 'edited the {{name}} contest',
    deleted: 'deleted the {{name}} contest',
    photo: 'changed the photo for the {{name}} contest',
  },
  Course: {
    created: 'created the {{title}} course',
    deleted: 'deleted the {{title}} course',
    edited: 'edited the {{title}} course',
    photo: 'changed the photo for the {{title}} course',
  },
  Generic: {
    bulk_load_user_levels: 'created UserLevel objects in bulk',
    bulk_create_resources: 'created Source objects in bulk',
    edit_user_levels: 'updated UserLevel {{label}}',
  },
  Group: {
    created: 'created a new group {{label}}',
    deleted: 'deleted group {{label}}',
    messaged: 'sent a message to the {{label}} group',
    edited: 'edited the {{label}} segment',
    welcomed: 'sent a welcome email to the {{label}} segment',
  },
  Post: {
    created: 'created the {{title}} post',
    deleted: 'deleted the {{title}} post',
    edited: 'edited the {{title}} post',
  },
  Question: {
    created: 'created a question with text {{text}}',
    deleted: 'created a question with text {{text}}',
    edited: 'created a question with text {{text}}',
    source: 'changed the sources for the question {{text}}',
    image: 'changed the image for the question {{text}}',
  },
  Card: {
    created: 'created the {{name}} card',
    deleted: 'deleted the {{name}} card',
    edited: 'edited the {{name}} card',
    updated: 'updated the {{name}} card',
  },
  Quiz: {
    created: 'created the {{title}} quiz',
    deleted: 'deleted the {{title}} quiz',
    edited: 'edited the {{title}} quiz',
    question: 'changed the questions for the {{title}} quiz',
    source: 'changed the sources for the {{title}} quiz',
    order: 'changed the question order for the {{title}} quiz',
    removed: 'removed the {{title}} quiz',
    photo: 'set a photo for the {{title}} quiz',
  },
  Source: {
    created: 'created the source {{title}}',
    deleted: 'deleted the source {{title}}',
    edited: 'edited the source {{title}}',
  },
  Resource: {
    created: 'created the source {{title}}',
    deleted: 'deleted the source {{title}}',
    edited: 'edited the source {{title}}',
    transcoded: 'transcoded the source {{title}}',
  },
  Settings: {
    updated_settings: 'updated settings',
    exported: 'exported data',
  },
  Message: {
    scheduled: 'has scheduled a message to {{recipient_count}} users.',
    sent: 'has sent a message to {{recipient_count}} users.',
  },
  Survey: {
    started: 'started the {{title}} survey',
    created: 'created the {{title}} survey',
    deleted: 'deleted the {{title}} survey',
    extended: 'extended the {{title}} survey',
    ended_early: '{{title}} survey ended early',
    messaged: 'messaged the {{title}} survey',
    edited: 'edited the {{title}} survey',
    updated: 'updated the {{title}} survey',
  },
  Tag: {
    created: 'created a new tag {{label}}',
    deleted: 'deleted the {{label}} tag',
    edited: 'edited the {{label}} tag',
  },
  Topic: {
    created: 'created the topic {{title}}',
    deleted: 'deleted the topic {{title}}',
    removed: 'removed the topic {{title}}',
    photo: 'set a photo for the topic {{title}}',
    edited: 'edited the topic {{title}}',
  },
  User: {
    deactivated: 'deactivated the user {{user_name}}',
    activated: 'reactivated the user {{user_name}}',
    rejected_terms: '{{name}} rejected the hourly warning terms',
    consented: '{{edited_user_name}} consented to receive SMS messages from Trivie',
    created: 'created a new user {{edited_user_name}}',
    deleted: 'deleted the user {{edited_user_name}}',
    edited: 'edited the user {{edited_user_name}}',
    messaged: 'sent a message to user {{edited_user_name}}',
    welcomed: 'sent a welcome email to user {{edited_user_name}}',
    password: 'sent a password reset email to user {{edited_user_name}}',
    invited: 'invited user {{edited_user_name}} to Trivie',
    set_roles: 'edited user role as {{role_names}}',
    login: '{{user_name}} logged in',
  },
  UserBadge: {
    created: 'created a badge {{name}}',
    edited: 'edited the {{name}} badge',
    deleted: 'deleted the {{name}} badge',
    revoked: 'revoked the {{name}} badge',
  },
  UserLevel: {
    created: 'created UserLevel {{label}}',
    deleted: 'deleted UserLevel {{label}}',
    icon: 'updated icon for UserLevel {{label}}',
    updated: 'updated UserLevel {{label}}',
  },
  Workflow: {
    created: 'created the {{workflow_name}} workflow',
    deleted: 'deleted the {{workflow_name}} workflow',
    edited: 'edited the {{workflow_name}} workflow',
  },
  Path: {
    created: 'created the {{path_name}} path',
    deleted: 'deleted the {{path_name}} path',
    edited: 'edited the {{path_name}} path',
    photo: 'set a photo for the {{path_name}} path',
  },
  SubscriptionProduct: {
    created: 'created a new subscription titled {{product_title}}',
    edited: 'edited the {{product_title}} subscription',
    archived: 'archived the {{product_title}} subscription',
  },
  SubscriptionPurchase: {
    purchase: 'purchased a subscription to {{product_title}}',
    renewal: 'subscription to {{product_title}} renewed',
    cancelled: 'subscription to {{product_title}} cancelled',
    gifted: 'gifted {{user_name}} a subscription to {{product_title}}',
    invited: 'invited {{user_count}} user(s) and gifted them a subscription to {{product_title}}',
    revoked: 'had their subscription revoked from {{product_title}}',
    reattempted: 'reattempted the purchase of {{product_title}}',
    extended: 'extended the expiration date for purchase {{purchase_id}}',
  },
  UserRole: {
    granted: '{{user_name}}',
    revoked: '{{user_name}}',
  },
}

const getLink = (type: string, id: number | null, mainTab = 'scheduled') => {
  if (type === 'deleted') {
    return null
  }

  switch (type) {
    case 'Assignment':
      return `/activities/assignments/${mainTab}/${id}/overview`
    case 'Message':
      return `/activities/messages/all/?message_id=${id}`
    case 'Contest':
      return null
    case 'Course':
      return `/content/topics/${id}/overview`
    case 'Generic':
      return null
    case 'Group':
      return `/people/groups/${id}/overview`
    case 'Post':
      return null
    case 'Question':
      return `/content/questions/${id}`
    case 'Quiz':
      return `/content/quizzes/${id}/overview`
    case 'Source':
      return `/content/sources/${id}`
    case 'Resource':
      return `/content/sources/${id}`
    case 'Settings':
      return null
    case 'Tag':
      return `/people/tags/${id}/overview`
    case 'Topic':
      return `/content/topics/${id}/overview`
    case 'Path':
      return `/content/paths/${id}/overview`
    case 'SubscriptionProduct':
      return id ? `/activities/subscriptions/${id}/overview` : null
    case 'SubscriptionPurchase':
      return id ? `/activities/subscriptions/purchases/${id}/overview` : null
    case 'User':
      return id ? `/people/users/${id}/overview` : `/people/users`
    case 'UserLevel':
      return null
  }
}

export function activityTransformer(activities: Array<Instance<typeof RawUserActivity>>) {
  const today = DateTime.local().startOf('day')
  const yesterday = DateTime.local().plus({ day: -1 }).startOf('day')

  return activities
    .sort((a, b) => {
      return b.id - a.id
    })
    .map((activity) => {
      const text: string | undefined = (eventStrings as any)[activity.object_type]?.[
        activity.action
      ]
      if (!text) {
        console.warn('no text for object/action', activity.object_type, activity.action)
      }

      const tokenRegex = /\{\{(\w+)\}\}/g
      const matches = text ? Array.from(text.matchAll(tokenRegex)) : []
      const lookup = (key: string) =>
        (activity.text_map_values[key as keyof TextMapValueModel] as string | number | undefined) ??
        ''

      let firstPart: string | undefined = text
      let linkLabel: string | number | null = null
      let lastPart: string | null = null

      if (text && matches.length > 0) {
        const last = matches[matches.length - 1]
        const lastStart = last.index!
        const lastEnd = lastStart + last[0].length

        firstPart = text.slice(0, lastStart).replace(tokenRegex, (_, key) => String(lookup(key)))

        linkLabel = lookup(last[1]) || null
        const tail = text.slice(lastEnd)
        lastPart = tail.length > 0 ? tail : null
      }

      const event = DateTime.fromISO(activity.datetime_occurred)

      const a = {
        id: activity.id,
        name:
          activity.object_type === 'UserRole'
            ? activity.text_map_values.admin_name
            : activity.object_type === 'Message' ||
                activity.object_type === 'Path' ||
                activity.object_type === 'Workflow' ||
                activity.object_type === 'Source'
              ? activity.text_map_values.admin
              : activity.text_map_values.admin_name ||
                activity.text_map_values.admin ||
                activity.text_map_values.user_full_name ||
                activity.text_map_values.user_name ||
                activity.text_map_values.user,
        photo:
          activity.source_user_photo_url || activity.category.full_url || activity.user_photo_url,
        datetime_occurred: activity.datetime_occurred,
        marginFix:
          (activity.object_type === 'User' &&
            (activity.action === 'consented' || activity.action === 'login')) ||
          activity.action === 'rejected_terms',
        link: {
          name:
            activity.object_type === 'UserRole'
              ? `${activity.text_map_values.admin_name} ${activity.action} the ${activity.text_map_values.role_name} role ${activity.action === 'granted' ? 'to' : 'from'}`
              : activity.object_type === 'User' &&
                  (activity.action === 'consented' || activity.action === 'login')
                ? ''
                : activity.object_type === 'Message' ||
                    activity.object_type === 'Path' ||
                    activity.object_type === 'Workflow' ||
                    activity.object_type === 'Source'
                  ? activity.text_map_values.admin
                  : activity.text_map_values.user_full_name ||
                    activity.text_map_values.user ||
                    activity.text_map_values.admin ||
                    activity.text_map_values.admin_name,
          firstPart,
          link: getLink(activity.object_type, activity.object_id),
          fullLink:
            activity.object_type === 'Message'
              ? getLink(activity.object_type, activity.object_id)
              : '',
          linkLabel,
          lastPart,
        },
        ...(event > today
          ? { elapsedTime: elapsedTime(activity.datetime_occurred, 'long') }
          : {
              elapsedTime: DateTime.fromISO(activity.datetime_occurred).toLocaleString(
                DateTime.TIME_SIMPLE,
              ),
            }),
      }

      return a
    })
    .reduce((acc: { [key: string]: Array<typeof activity> }, activity) => {
      const event = DateTime.fromISO(activity.datetime_occurred)

      let key = 'Today'

      if (event < yesterday) {
        key = event.toFormat('EEEE, MMMM dd').toLocaleUpperCase()
      } else if (event < today) {
        key = 'Yesterday'
      }

      if (!acc) {
        acc = {
          [key]: [activity],
        }
      } else if (!acc[key]) {
        acc[key] = [activity]
      } else {
        acc[key].push(activity)
      }

      return acc
    }, {})
}
